import React, { useCallback, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { registerUser } from '../../queries';
import './AuthModal.scss';

interface RegisterModalProps {
  show: boolean;
  onHide: () => void;
  onSwitchToLogin: () => void;
}

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const RegisterModal: React.FC<RegisterModalProps> = ({ show, onHide, onSwitchToLogin }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reset = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
  }, []);

  const handleHide = useCallback(() => {
    reset();
    onHide();
  }, [reset, onHide]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError('');

      if (firstName.trim().length < 2) {
        setError('First name must be at least 2 characters.');
        return;
      }
      if (lastName.trim().length < 2) {
        setError('Last name must be at least 2 characters.');
        return;
      }
      if (!isValidEmail(email)) {
        setError('Please enter a valid email address.');
        return;
      }
      if (!password) {
        setError('Please enter a password.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      setLoading(true);
      try {
        const result = await registerUser({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          emailId: email.trim(),
          passwd: password,
        });
        if (result.success) setSuccess(true);
        else setError(result.message || 'Registration failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [firstName, lastName, email, password, confirmPassword],
  );

  return (
    <Modal show={show} onHide={handleHide} centered dialogClassName="auth-modal">
      <Modal.Header closeButton>
        <Modal.Title>Create an account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <div>
            <p>Account created! You can now log in.</p>
            <Button
              variant="primary"
              onClick={() => {
                handleHide();
                onSwitchToLogin();
              }}
            >
              Go to login
            </Button>
          </div>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                autoComplete="given-name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                autoComplete="family-name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
              />
            </Form.Group>
            {error && <p className="text-danger">{error}</p>}
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </Button>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};
